// Audit log view — admin-only, shows all peer state transitions

import { useEffect, useState } from "react";
import { DataTable } from "../components/DataTable.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { api, type AuditEntry } from "../lib/api-client.js";

export function AuditLog() {
	const [entries, setEntries] = useState<AuditEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [ipFilter, setIpFilter] = useState("");

	const load = async () => {
		setLoading(true);
		try {
			const { entries } = await api.getAuditLog({
				ip: ipFilter || undefined,
				limit: 200,
			});
			setEntries(entries);
		} catch {
			// handled by api-client redirect
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		load();
	}, []);

	const columns = [
		{
			key: "time",
			header: "Time",
			render: (e: AuditEntry) => new Date(e.createdAt).toLocaleString(),
		},
		{
			key: "ip",
			header: "Peer IP",
			render: (e: AuditEntry) => <span className="mono">{e.ipAddress}</span>,
		},
		{
			key: "action",
			header: "Action",
			render: (e: AuditEntry) => e.action,
		},
		{
			key: "from",
			header: "From",
			render: (e: AuditEntry) =>
				e.oldStatus ? (
					<StatusBadge status={e.oldStatus as "unassigned" | "assigned" | "revoked"} />
				) : (
					"—"
				),
		},
		{
			key: "to",
			header: "To",
			render: (e: AuditEntry) => (
				<StatusBadge status={e.newStatus as "unassigned" | "assigned" | "revoked"} />
			),
		},
		{
			key: "actor",
			header: "Actor",
			render: (e: AuditEntry) => e.actor,
		},
		{
			key: "reason",
			header: "Reason",
			render: (e: AuditEntry) => e.reason || "—",
		},
	];

	return (
		<div>
			<h1 className="page-title">Audit Log</h1>

			<div className="toolbar">
				<input
					className="form-input"
					style={{ width: "200px" }}
					type="text"
					placeholder="Filter by IP..."
					value={ipFilter}
					onChange={(e) => setIpFilter(e.target.value)}
				/>
				<button className="btn" onClick={load} type="button">
					Search
				</button>
			</div>

			{loading ? (
				<div style={{ color: "var(--color-ink-muted)" }}>Loading...</div>
			) : (
				<DataTable columns={columns} data={entries} rowKey={(e) => e.id} />
			)}
		</div>
	);
}
